import { FormControl, Autocomplete, Box, TextField, CircularProgress } from '@mui/material';
import Dot from 'components/common/dot/Dot';
import UserAvatar from 'components/common/userAvatar/UserAvatar';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import useUserData, { UserType } from 'hooks/useUserData';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import useCompanyQueryData from 'hooks/useCompanyQueryData';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { uniqBy, uniqueId } from 'lodash';
import { SuggestionContactType, useSuggestionContactsQuery } from '@buildwise/libs/company-admin';
import { useLocation } from 'react-router-dom';
import { ConfigEntryType } from 'config/config';

type UserOptionType = {
  id: string;
  primaryCompanyName?: string;
  profileImageURL?: string;
  userNameToDisplay: string;
  userPrimaryCompanyName?: string;
  title: string;
  username: string;
} & {
  label: any;
  value: string;
};

type UsersSearchResponseType = {
  totalElements?: number;
  totalNumber?: number;
  totalPages?: number;
  userResponse: UserType[];
};

type QuestionAssignedToFieldProps = {
  autoFocus?: boolean;
  departments: string[];
  regions: string[];
  field: ConfigEntryType;
};

function SuggestionAssignedToField({
  autoFocus,
  departments,
  regions,
  field,
}: QuestionAssignedToFieldProps) {
  const { shouldDisplayCompanyOnUserCards } = useCompanyQueryData();
  const { user, companyId } = useUserData();
  const { pathname } = useLocation();
  const { suggestionContacts, isLoading } = useSuggestionContactsQuery({
    companyId,
    ...(!pathname.includes('suggestion-contacts') && { shouldFetch: false }),
  });
  const {
    control,
    formState: { isSubmitting },
    setValue,
  } = useFormContext<PostFormData>();
  const {
    data: peopleData,
    isLoading: peopleLoading,
    isFetched,
  } = useSearchItemQuery<UsersSearchResponseType>(
    {
      searchType: 'users',
      operator: 'or',
      filterBy: {
        filters: [
          {
            filterType: 'name',
            filterValue: 'ALL',
          },
        ],
      },
    },
    pathname.includes('suggestion-contacts'),
  );
  const taskOwner: string = useWatch({
    control,
    name: 'taskOwner',
  });
  const defaultValue = useMemo(
    () =>
      taskOwner
        ? {
            id: uniqueId(),
            primaryCompanyName: '',
            profileImageURL: '',
            userNameToDisplay: taskOwner,
            userPrimaryCompanyName: '',
            title: '',
            username: taskOwner,
            label: taskOwner,
            value: taskOwner,
          }
        : null,
    [],
  );
  const [fieldValue, setFieldValue] = useState<UserOptionType | null>(defaultValue);
  const { peopleOptions }: { peopleOptions: UserOptionType[] } = useMemo(() => {
    // Helper function to filter users or suggestion contacts by departments and regions
    const filterByCriteria = (
      _user: UserType | SuggestionContactType,
      isSuggestionContact: boolean = false,
    ) => {
      const matchesDepartments = isSuggestionContact
        ? departments.includes((_user as SuggestionContactType).groupName)
        : departments.every((value) => (_user as UserType)?.department?.includes(value));

      const matchesRegions = isSuggestionContact
        ? regions.includes((_user as SuggestionContactType).region)
        : regions.every((value) => (_user as UserType)?.region?.includes(value));

      return (
        ((departments.length && matchesDepartments) || (regions.length && matchesRegions)) &&
        (!departments.length || !regions.length || (matchesDepartments && matchesRegions))
      );
    };

    let options: UserOptionType[] = [];

    // Process peopleData for "suggestion-contacts" path
    if (pathname.includes('suggestion-contacts')) {
      options = (departments?.length || regions?.length ? peopleData?.userResponse ?? [] : [])
        .filter((_user) => filterByCriteria(_user))
        .map((userData: UserType) => ({
          ...userData,
          label: `${userData?.userNameToDisplay} - ${userData?.email}`,
          value: userData.username,
        }));
    } else {
      // Process peopleData for other paths
      options = (peopleData?.userResponse ?? []).map((user: UserType) => ({
        id: user.id,
        primaryCompanyName: user.primaryCompanyName,
        profileImageURL: user.profileImageURL,
        userNameToDisplay: user.userNameToDisplay,
        title: user.title,
        username: user.username,
        label: `${user.userNameToDisplay} - ${user.username}`,
        value: user.username,
      }));
    }

    return {
      peopleOptions: defaultValue ? uniqBy([...options, defaultValue], 'value') : options,
    };
  }, [peopleData, departments, regions, pathname]);

  const handleSelectUsers = useCallback(
    (_: SyntheticEvent<Element, Event>, user: UserOptionType | null) => {
      setValue('taskOwner', user?.value || '', { shouldValidate: true });
      setFieldValue(user);
    },
    [],
  );

  useEffect(() => {
    if (peopleOptions.length) {
      setFieldValue(peopleOptions?.find((option) => option?.value === taskOwner) || fieldValue);
    }
  }, [peopleData]);

  useEffect(() => {
    // Check if the current taskOwner is still a valid option
    const matchingOption = peopleOptions?.find((option) => option?.value === taskOwner);

    if (isFetched && !matchingOption) {
      setFieldValue(null);
      setValue('taskOwner', '');
    }
  }, [departments, regions, peopleOptions, taskOwner, isFetched]);

  return (
    <Controller
      control={control}
      name="taskOwner"
      render={({ field: { value, ref, ...rest }, fieldState }) => (
        <FormControl fullWidth>
          <Autocomplete
            {...rest}
            id="taskOwner"
            onChange={handleSelectUsers}
            value={fieldValue}
            options={peopleOptions}
            filterOptions={(options, state) =>
              options.filter(
                (option) =>
                  option.userNameToDisplay.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                  option.username.toLowerCase().includes(state.inputValue.toLowerCase()),
              )
            }
            fullWidth
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <UserAvatar
                  disableUserLink
                  avatarSize={40}
                  profileImageURL={option.profileImageURL}
                  title={`${option.userNameToDisplay} (${option.username})`}
                  avatarName={option.userNameToDisplay}
                  subTitle={
                    <>
                      {option.title}
                      {shouldDisplayCompanyOnUserCards &&
                        (option?.primaryCompanyName || option?.userPrimaryCompanyName) && (
                          <>
                            <Dot />
                            {option?.primaryCompanyName || option?.userPrimaryCompanyName}
                          </>
                        )}
                    </>
                  }
                  userName={option.username}
                />
              </Box>
            )}
            autoHighlight
            noOptionsText={isLoading || peopleLoading ? 'Loading...' : 'No Users Found'}
            getOptionDisabled={(option) =>
              option?.username === user?.username ||
              (pathname.includes('suggestion-contacts') &&
                !!suggestionContacts.find(
                  (contact) =>
                    contact?.groupName === departments?.[0] &&
                    contact?.region === regions?.[0] &&
                    contact?.owner === option?.username,
                ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={ref}
                placeholder={`${field?.referenceObject?.placeHolder || 'Select'}`}
                autoFocus={autoFocus}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading || peopleLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                variant="standard"
              />
            )}
            disabled={isSubmitting}
          />
        </FormControl>
      )}
    />
  );
}

export default SuggestionAssignedToField;
